import { useMemo, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_APP_CFG, UPDATE_USER_SETTINGS_AUTH } from '@scio3/graphql-client';
import type { BannerPromotionItem, AppCfgBannerResponse } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { usePermissions } from '@/hooks/usePermissions';
import dayjs from 'dayjs';

type BannerType = 'trial' | 'free' | 'paid';

interface PromoCfgResponse {
  getAppCfgs: AppCfgBannerResponse | null;
}

/** Master toggles from appCfgSCIO document (distinct from company.payment fields) */
interface ScioCfgResponse {
  getAppCfgs: {
    trialBanner?: boolean; // master on/off toggle for trial banners
    bannerPromotionHTML?: unknown; // truthy = enable paid promo banner
  } | null;
}

interface UpdateUserSettingsResponse {
  updateUserSettingsAuth: {
    _id: string;
    isHideBannerPromotionForPaidUser?: boolean;
  };
}

/** Pages where banners should NOT show (matching SCIO1) */
const EXCLUDED_PATHS = ['/app/designManagement'];

/**
 * Weighted random selection for promotional banners (SCIO1 format)
 * Selects one message from array based on percent weights
 */
function selectWeightedBanner(items: BannerPromotionItem[] | undefined): string | null {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return null;
  }

  const totalWeight = items.reduce((sum, item) => sum + (item.percent || 0), 0);
  if (totalWeight === 0) {
    return items[0]?.text || null;
  }

  const random = Math.random() * totalWeight;
  let cumulative = 0;

  for (const item of items) {
    cumulative += item.percent || 0;
    if (random < cumulative) {
      return item.text;
    }
  }

  return items[items.length - 1]?.text || null;
}

export function usePromotionalBanner() {
  const location = useLocation();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const { isRootAdmin } = usePermissions();

  const [localDismissed, setLocalDismissed] = useState(false);
  // Cache key tracks appCfg + account type — re-randomize only when these change
  const selectedBannerRef = useRef<string | null>(null);
  const lastCacheKeyRef = useRef<string>('');

  // Fetch promotional banner content (appType: 'promotionSCIO')
  const { data: promoCfgData } = useQuery<PromoCfgResponse>(GET_APP_CFG, {
    variables: { teamId: activeTeamId, appType: 'promotionSCIO' },
    skip: !activeTeamId,
  });

  // Fetch master toggles (appType: 'scio') — GAP 5, 9
  const { data: scioCfgData } = useQuery<ScioCfgResponse>(GET_APP_CFG, {
    variables: { teamId: activeTeamId, appType: 'scio' },
    skip: !activeTeamId,
  });

  const [updateUserSettings] = useMutation<UpdateUserSettingsResponse>(UPDATE_USER_SETTINGS_AUTH);

  // Account type checks
  const isTrial = company?.payment?.isTrial ?? false;
  const isActive = company?.payment?.isActive ?? false;
  const isFreePlan = company?.payment?.planFeature === 'free'; // GAP 1: was 'FREE_PLAN'
  const isWhiteLabel = !!company?.brandingData?.isWhiteLabel; // GAP 6
  const isHideBannerFromBackend = !!(me?.userSettings as Record<string, unknown> | undefined)
    ?.isHideBannerPromotionForPaidUser;

  // Master toggles from appCfgSCIO — GAP 5, 9
  const scioTrialBannerEnabled = !!scioCfgData?.getAppCfgs?.trialBanner;
  const scioPaidBannerEnabled = !!scioCfgData?.getAppCfgs?.bannerPromotionHTML;

  // Trial dismissal tri-state logic — GAP 4
  const trialEndDate = company?.payment?.trialEndDate;
  const trialBannerValue = company?.payment?.trialBanner ?? 0;
  const daysLeft = trialEndDate ? Math.round(dayjs(trialEndDate).diff(dayjs(), 'day', true)) : 0;
  // Tri-state dismissal from company.payment (NOT the appCfgSCIO master toggle):
  // 0 = show, 1 = dismissed (re-show when ≤7 days remain), 2 = permanently dismissed
  const trialDismissalAllows = trialBannerValue === 0 || (trialBannerValue === 1 && daysLeft <= 7);
  // value === 2: permanently dismissed → trialDismissalAllows stays false

  // Path check — GAP 8: blocklist instead of allowlist
  const isExcludedPath = EXCLUDED_PATHS.some((path) => location.pathname.startsWith(path));
  // Hide global banner on Engage page when plan is NOT engage (scio2 parity)
  const isEngagePageNonEngagePlan =
    location.pathname.startsWith('/app/engageManagement') &&
    company?.payment?.planFeature !== 'engage';

  // Banner type determination (mutually exclusive, matching SCIO1 priority)
  const bannerType = useMemo((): BannerType | null => {
    // Wait for auth data to load — prevents flash of banner before dismissal state is known
    if (!me || !company) return null;
    // Common exclusions
    if (isWhiteLabel) return null;
    if (isExcludedPath) return null;
    if (isEngagePageNonEngagePlan) return null;

    // 1. Free plan — always show (no role check, matches SCIO1)
    if (isFreePlan) return 'free';

    // 2. Trial — requires admin + active + master toggle + dismissal allows (GAP 7)
    if (isTrial && isActive && isRootAdmin && scioTrialBannerEnabled && trialDismissalAllows) {
      return 'trial';
    }

    // 3. Paid — not trial, not free, not dismissed (GAP 3, 9)
    // Guard: trial users must never fall through to 'paid' (which has close button)
    if (
      !isTrial &&
      !isFreePlan &&
      !isHideBannerFromBackend &&
      !localDismissed &&
      scioPaidBannerEnabled
    ) {
      return 'paid';
    }

    return null;
  }, [
    me,
    company,
    isWhiteLabel,
    isExcludedPath,
    isEngagePageNonEngagePlan,
    isFreePlan,
    isTrial,
    isActive,
    isRootAdmin,
    scioTrialBannerEnabled,
    trialDismissalAllows,
    isHideBannerFromBackend,
    localDismissed,
    scioPaidBannerEnabled,
  ]);

  // Select banner HTML using weighted random.
  // Cached via composite key (appCfg ref + account type) to prevent re-randomization
  // on unrelated re-renders, while still re-selecting when account type changes.
  const bannerHtml = useMemo(() => {
    const appCfg = promoCfgData?.getAppCfgs;
    const cacheKey = `${isFreePlan}:${isTrial}:${appCfg ? 'loaded' : 'none'}`;

    if (cacheKey !== lastCacheKeyRef.current) {
      lastCacheKeyRef.current = cacheKey;

      if (!appCfg) {
        selectedBannerRef.current = null;
      } else if (isFreePlan && appCfg.trialBannerFreePlanHtml) {
        selectedBannerRef.current = selectWeightedBanner(appCfg.trialBannerFreePlanHtml);
      } else if (isTrial && appCfg.trialBannerPromotionHtml) {
        selectedBannerRef.current = selectWeightedBanner(appCfg.trialBannerPromotionHtml);
      } else if (!isTrial && !isFreePlan && appCfg.bannerPromotionHTML) {
        selectedBannerRef.current = selectWeightedBanner(appCfg.bannerPromotionHTML);
      } else {
        selectedBannerRef.current = null;
      }
    }

    return selectedBannerRef.current;
  }, [promoCfgData, isTrial, isFreePlan]);

  const isVisible = bannerType !== null && (bannerType === 'trial' || !!bannerHtml);

  // Upgrade button: shown for trial AND free plan (admin only)
  const showUpgrade = isRootAdmin && (bannerType === 'trial' || bannerType === 'free');

  const dismiss = useCallback(() => {
    setLocalDismissed(true);

    // Persist to backend (only for paid users)
    if (!isTrial && !isFreePlan) {
      updateUserSettings({
        variables: {
          updateData: {
            isHideBannerPromotionForPaidUser: true,
          },
          teamId: activeTeamId,
        },
      }).catch(() => {
        // Best-effort: local dismiss is sufficient UX, backend persistence optional
      });
    }
  }, [updateUserSettings, activeTeamId, isTrial, isFreePlan]);

  return {
    isVisible,
    bannerType,
    bannerHtml,
    daysLeft,
    showUpgrade,
    dismiss,
  };
}
